/**
 * Der Schalter für die Antwort-Tiefe unter dem Eingabefeld.
 * (27.09.2026, Tickets 2671/2673/2678)
 *
 * Martin, 26.09.2026: *„Neben der Werkzeug-Anzeige soll ein kleiner
 * Button sitzen, analog zur Modell-Auswahl, mit dem man den Antwort-Modus
 * umschalten kann: Kompakt / Normal / Ausführlich"* — und: *„Platz sparen,
 * nicht ausschreiben."*
 *
 * Gebaut wie die Modell-Auswahl daneben: dieselbe Pille, ein Tippen öffnet
 * dieselbe Auswahl (selectSheet), gespeichert wird über denselben Weg wie
 * jede Einstellung (speichereEinstellungen → PUT /api/settings).
 *
 * ⚠️ EIN Besitzer: der Zustand ist `users.settings.antwortTiefe` am Server.
 *    Hier gibt es keinen eigenen — der Schalter liest bei jedem Zeichnen den
 *    Einstellungs-Zwischenspeicher, den nur der Abgleich und
 *    speichereEinstellungen beschreiben. Schaltet das Gespräch um (Werkzeug
 *    antwortmodus_setzen), holt nachWerkzeug den Serverstand; dasselbe tut
 *    der Abgleich beim Zurückkehren in die App.
 */
import {
  ANTWORT_TIEFEN,
  ANTWORT_TIEFE_VORGABE,
  istAntwortTiefe,
  type AntwortTiefe,
} from "../../shared/antwort-tiefe.js";
import { gleicheAb } from "./einstellungen-abgleich.js";
import type { SpeicherErgebnis } from "./einstellungen-ausstehend.js";
import { loadSettings, speichereEinstellungen } from "./settings.js";
import { baueSchnellEinstellungen } from "./schnell-einstellungen.js";

/** Das Gesprächswerkzeug, das dieselbe Einstellung schreibt (tools/antwortmodus.ts). */
export const TIEFE_WERKZEUG = "antwortmodus_setzen";

/**
 * So heißt jede Stufe in der Oberfläche — auf der Pille und in der Auswahl.
 *
 * 27.09.2026 (Ticket 2696, Martin: *„damit klar erkennbar ist, dass es
 * sich um kurz/mittel/lang (Detailgrad) handelt"*): seine Worte. Bis hierher
 * „Kurz / Normal / Ausführlich" — mit dem Wort auf der Pille wäre sie bei
 * „Ausführlich" 99 Punkte breit und hätte am Telefon (390) nach einem Zug
 * mit Werkzeugen den Modell-Chip daneben gekürzt; „Lang" braucht 64.
 * Die gespeicherten Werte und das Gesprächswerkzeug bleiben bei
 * kurz/normal/ausfuehrlich.
 *
 * 01.10.2026 (Ticket 2889): die vierte Stufe, „ultrakurz“. Auf der Pille
 * steht „Ultra“ (5 Buchstaben, schmaler als „Mittel“ — die Breitenregeln in
 * style.css rechnen mit dem breitesten Wort und bleiben gültig); Ansage,
 * Titel und Auswahl nennen `voll`: „Ultra-kurz“.
 */
export const TIEFE_TEXT: Readonly<Record<AntwortTiefe, { name: string; voll: string; was: string }>> = {
  ultrakurz: { name: "Ultra", voll: "Ultra-kurz", was: "Ein Satz" },
  kurz: { name: "Kurz", voll: "Kurz", was: "Nur der Kern" },
  normal: { name: "Mittel", voll: "Mittel", was: "Wie gewohnt" },
  ausfuehrlich: { name: "Lang", voll: "Lang", was: "Mit Beispielen" },
};

/** Die Stufe, die gerade gilt — ohne Eintrag „normal“, wie am Server. */
function aktuelleTiefe(): AntwortTiefe {
  const s = loadSettings().antwortTiefe;
  return istAntwortTiefe(s) ? s : ANTWORT_TIEFE_VORGABE;
}

/**
 * Den Schalter auf den Stand bringen: das Wort der Stufe steht sichtbar in
 * der Pille, die Textzeilen davor wählt `data-stufe` in style.css. Vorleser
 * hören dasselbe Wort im aria-label, die Maus liest es im Titel.
 *
 * ⚠️ 27.09.2026 (Ticket 2696): bis hierher nur ein Symbol aus drei
 *    stehenden Balken — Martin las es als Empfangsstärke.
 */
export function zeigeTiefe(el: HTMLElement | null): void {
  if (!el) return;
  const stufe = aktuelleTiefe();
  const t = TIEFE_TEXT[stufe];
  el.dataset.stufe = stufe;
  const wort = el.querySelector(".composer-tiefe-wort");
  if (wort) wort.textContent = t.name;
  el.setAttribute("aria-label", `Antwortlänge: ${t.voll}. Ändern`);
  el.title = `Antwortlänge: ${t.voll} — ${t.was}`;
  // 21.10.2026: EIN Chip für Modell, Länge und Werkzeuge — das Wort der Stufe steht als data-Wert am Modell-Chip
  // (style.css setzt es hinter den Modellnamen); der Chip öffnet dasselbe Blatt wie dieser Schalter.
  const chip = document.getElementById("status-llm");
  if (chip) {
    chip.dataset.tiefe = t.name;
    benenneChip(chip);
  }
}

/**
 * Der Name des Chips für Vorleser: beginnt mit dem sichtbaren Wort („Sonnet 5.5, Abo, Antwortlänge Mittel,
 * 2 Werkzeuge. Ändern“). Gelesen wird der Chip-Text, der Stufen-Wert und die Werkzeug-Zahl am Chip.
 */
export function benenneChip(chip: HTMLElement): void {
  const modell = (chip.textContent ?? "").replace(/\s*·\s*/g, ", ").trim();
  const stufe = TIEFE_TEXT[aktuelleTiefe()].voll;
  const w = Number(/\d+/.exec(chip.dataset.werkzeuge ?? "")?.[0] ?? 0);
  const teile = [modell, `Antwortlänge ${stufe}`];
  if (w > 0) teile.push(`${w} ${w === 1 ? "Werkzeug" : "Werkzeuge"}`);
  chip.setAttribute("aria-label", `${teile.filter(Boolean).join(", ")}. Ändern`);
}

/** Zieht den Namen nach, wenn irgendwer den Text des Chips ändert (Modellwechsel, Laufzeit-Auskunft, Ersatz). */
export function haeltChipNameAktuell(chip: HTMLElement | null): void {
  if (!chip) return;
  new MutationObserver(() => benenneChip(chip)).observe(chip, { childList: true, characterData: true, subtree: true });
  benenneChip(chip);
}

/**
 * Die Auswahl öffnen und die Wahl speichern. Gibt die Antwort des Servers
 * zurück, oder null, wenn nichts an der Antwortlänge geändert wurde.
 *
 * 29.09.2026 (Ticket 2736): Unter den drei Stufen stehen Vorlese-Tempo und
 * Emojis (lib/schnell-einstellungen.ts). Sie speichern selbst und lassen das
 * Blatt offen; die Pille in der Fußzeile bleibt, wie sie ist.
 */
export async function waehleTiefe(
  el: HTMLElement | null,
  vorab?: HTMLElement,
  titel = "Wie Vespera antwortet",
): Promise<SpeicherErgebnis | null> {
  const { selectSheet } = await import("./sheet-ui.js");
  const jetzt = aktuelleTiefe();
  // 20.10.2026: vier Gruppen — Länge (hier) · Tempo · Absenden nach Stille · Emojis (zusatz), je mit einem Satz Erklärung.
  // Sagen lässt sich nur die Länge (antwortmodus_setzen), nicht Tempo oder Emojis.
  const wahl = await selectSheet({
    title: titel,
    vorab,
    gruppe: { name: "Länge der Antwort", satz: "Gilt im Chat und an der Sprechtaste. Du kannst sie auch sagen." },
    aktiv: jetzt,
    options: ANTWORT_TIEFEN.map((stufe) => ({
      value: stufe,
      label: TIEFE_TEXT[stufe].voll,
      beschreibung: TIEFE_TEXT[stufe].was,
    })),
    zusatz: baueSchnellEinstellungen(),
  });
  if (!wahl || wahl === jetzt || !istAntwortTiefe(wahl)) return null;
  const antwort = speichereEinstellungen({ antwortTiefe: wahl });
  zeigeTiefe(el);
  return antwort;
}

/**
 * Nach einem Werkzeug-Ergebnis: Hat das Gespräch die Stufe umgestellt, den
 * Serverstand holen — der Abgleich legt ihn in den Zwischenspeicher und
 * meldet die Änderung, der Schalter zeichnet sich dann neu.
 */
export function nachWerkzeug(tool: string, isError: boolean): Promise<void> | null {
  if (tool !== TIEFE_WERKZEUG || isError) return null;
  return gleicheAb();
}
